import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

interface HoverTooltipProps {
  /** Rich card body rendered inside the dark surface. */
  content: React.ReactNode;
  /** Trigger element. */
  children: React.ReactNode;
  /** Card width in px (default 260). */
  width?: number;
  /** Show delay in ms (default 300). */
  delayMs?: number;
  /** Estimated card height used to decide flip-above (default 160). */
  estimatedHeight?: number;
  /** Left offset as a ratio of the trigger width, 0–1 (default 0). */
  alignRatio?: number;
  /**
   * Horizontal anchoring of the card relative to the trigger.
   * - "center" (default): card is centered on the trigger.
   * - "start": the card's left edge aligns with the trigger's left edge.
   */
  align?: "center" | "start";
  /**
   * Where the arrow points along the trigger width, 0–1 (default 0.5 = center).
   * e.g. 0.7 points the arrow at 70% across the trigger.
   */
  arrowAnchorRatio?: number;
  /**
   * Position the arrow as a fraction of the CARD width instead of the trigger.
   * When set, overrides `arrowAnchorRatio` (e.g. 0.2 = 20% from the card's left
   * edge). Useful when the card is offset from the trigger.
   */
  arrowCardRatio?: number;
  /** Tailwind class for the arrow's horizontal position (default "left-4"). */
  arrowOffsetClass?: string;
  /** Vertical gap in px between the cursor and the tooltip card (default 18). */
  gap?: number;
  /** Wrapper class on the trigger container (default "inline-flex"). */
  triggerClassName?: string;
  /** Whether the tooltip is disabled. */
  disabled?: boolean;
}

/**
 * Shared rich hover-card tooltip — the dark `bg-primary` surface, arrow,
 * portal positioning and show/hide delay used by entity tooltips
 * (test cases, requirements, …). Pass the card body via `content`.
 *
 * Positioning is cursor-anchored: the card appears at the pointer where you
 * hover and flips above/below and shifts left/right to stay inside the
 * viewport. The legacy `align` / `alignRatio` / `arrowAnchorRatio` /
 * `arrowCardRatio` / `arrowOffsetClass` props are still accepted for backward
 * compatibility but no longer affect placement.
 */
export function HoverTooltip({
  content,
  children,
  width = 260,
  delayMs = 300,
  estimatedHeight = 160,
  gap: gapPx = 18,
  triggerClassName = "inline-flex",
  disabled = false,
}: HoverTooltipProps) {
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState<{
    top: number;
    left: number;
    arrowLeft: number;
    arrowSide: "top" | "bottom";
  } | null>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const pointerRef = useRef({ x: 0, y: 0 });
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const trackPointer = useCallback((e: React.MouseEvent) => {
    pointerRef.current = { x: e.clientX, y: e.clientY };
  }, []);

  const showTooltip = useCallback(
    (e: React.MouseEvent) => {
      if (disabled) return;
      pointerRef.current = { x: e.clientX, y: e.clientY };
      clearTimeout(timeoutRef.current);
      timeoutRef.current = setTimeout(() => setVisible(true), delayMs);
    },
    [delayMs, disabled],
  );

  const hideTooltip = useCallback(() => {
    clearTimeout(timeoutRef.current);
    setVisible(false);
    setCoords(null);
  }, []);

  // Anchor the card to the cursor, then flip above/below and shift left/right
  // so it always stays inside the viewport. Measured from the real rendered
  // card (via the portal wrapper, which has no transform) so the flip decision
  // uses the true height instead of a guess.
  useLayoutEffect(() => {
    if (!visible) return;
    const el = wrapperRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const cw = rect.width || width;
    const ch = rect.height || estimatedHeight;
    const { x, y } = pointerRef.current;
    const margin = 8;
    const gap = gapPx;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    // Vertical — below the cursor when it fits, otherwise above.
    const fitsBelow = y + gap + ch + margin <= vh;
    const fitsAbove = y - gap - ch - margin >= 0;
    const placeBelow = fitsBelow || !fitsAbove;
    let top = placeBelow ? y + gap : y - gap - ch;
    top = Math.max(margin, Math.min(top, vh - ch - margin));

    // Horizontal — start just right of the cursor, then clamp into view.
    let left = x - 14;
    if (left + cw > vw - margin) left = vw - margin - cw;
    if (left < margin) left = margin;

    setCoords({
      top,
      left,
      arrowLeft: Math.max(16, Math.min(cw - 16, x - left)),
      arrowSide: placeBelow ? "top" : "bottom",
    });
  }, [visible, width, estimatedHeight, gapPx]);

  useEffect(() => () => clearTimeout(timeoutRef.current), []);

  if (disabled) {
    return <div className={triggerClassName}>{children}</div>;
  }

  return (
    <div
      className={triggerClassName}
      onMouseEnter={showTooltip}
      onMouseMove={trackPointer}
      onMouseLeave={hideTooltip}
    >
      {children}
      {visible &&
        createPortal(
          <div
            ref={wrapperRef}
            className="pointer-events-none fixed z-[9999]"
            style={{
              top: coords?.top ?? 0,
              left: coords?.left ?? 0,
              maxWidth: width,
              // Hidden for the single measure pass, then shown in place — the
              // layout effect runs before paint so there's no flash at (0,0).
              visibility: coords ? "visible" : "hidden",
            }}
          >
            {/* Popover surface with theme-aware background, text, border and shadow */}
            <div
              className="relative animate-in space-y-1.5 rounded-lg border border-border bg-popover p-3 text-popover-foreground shadow-lg duration-150 fade-in-0 zoom-in-95"
              style={{ width }}
            >
              {/* Caret points back at the cursor; flips to the card's bottom
                  edge when the card is placed above the cursor. */}
              <div
                className={`absolute h-3 w-3 -translate-x-1/2 rotate-45 border-border bg-popover ${
                  coords?.arrowSide === "bottom"
                    ? "-bottom-1.5 border-r border-b"
                    : "-top-1.5 border-l border-t"
                }`}
                style={{ left: coords?.arrowLeft ?? 16 }}
              />
              {content}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}

export default HoverTooltip;
